import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, FileUp, ShieldCheck } from "lucide-react";
import { cnpjValido, cpfValido, telefoneValido } from "@/lib/validacao-cadastro";

function FF({ label, v, on, testid, type = "text", req, ac, nm }) {
  const [ro, setRo] = useState(!!ac);
  return (
    <div>
      <Label className="label-cap">{label}{req && " *"}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
        autoComplete={ac} name={nm} readOnly={ro} onFocus={() => setRo(false)}
        className="mt-2" data-testid={testid} />
    </div>
  );
}

function FileField({ label, onChange, testid, multiple }) {
  return (
    <div className="border border-dashed border-border p-3">
      <Label className="label-cap flex items-center gap-1"><FileUp size={12} /> {label} *</Label>
      <input type="file" accept=".pdf,.png,.jpg,.jpeg,.jfif,.webp,.heic" multiple={multiple}
        onChange={(e) => onChange(multiple ? Array.from(e.target.files || []) : e.target.files?.[0] || null)}
        className="mt-2 text-xs block w-full" data-testid={testid} />
    </div>
  );
}

export default function CadastroMontadora() {
  const navigate = useNavigate();
  const [f, setF] = useState({ nome: "", nome_fantasia: "", cnpj: "", responsavel: "", cpf_responsavel: "",
    telefone: "", whatsapp: "", email: "", password: "", endereco: "", pix: "" });
  const [docs, setDocs] = useState({ cartao_cnpj: null, contrato_social: null, comprovante_bancario: null, docs_socios: [] });
  const [busy, setBusy] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const enviar = async () => {
    if (!cnpjValido(f.cnpj)) {
      toast.error("CNPJ inválido — confira os 14 dígitos e os dígitos verificadores"); return;
    }
    for (const k of ["nome", "responsavel", "telefone", "email", "password"]) {
      if (!f[k]) { toast.error("Preencha todos os campos obrigatórios"); return; }
    }
    if (f.cpf_responsavel && !cpfValido(f.cpf_responsavel)) {
      toast.error("CPF do responsável inválido — confira os dígitos"); return;
    }
    if (!telefoneValido(f.telefone)) {
      toast.error("Telefone inválido — use DDD + número (ex.: 11 98888-7777)"); return;
    }
    if (f.whatsapp && !telefoneValido(f.whatsapp)) {
      toast.error("WhatsApp inválido — use DDD + número (ex.: 11 98888-7777)"); return;
    }
    if (!docs.cartao_cnpj || !docs.contrato_social || !docs.comprovante_bancario || docs.docs_socios.length === 0) {
      toast.error("Anexe todos os documentos obrigatórios (CNPJ, contrato social, comprovante bancário e documentos dos sócios)");
      return;
    }
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v || ""));
    fd.append("cartao_cnpj", docs.cartao_cnpj);
    fd.append("contrato_social", docs.contrato_social);
    fd.append("comprovante_bancario", docs.comprovante_bancario);
    docs.docs_socios.forEach((d) => fd.append("docs_socios", d));
    setBusy(true);
    try {
      await api.post("/auth/register-montadora", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEnviado(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (enviado) return (
    <div className="min-h-screen flex items-center justify-center p-6 text-center bg-white">
      <div className="max-w-md" data-testid="cadm-enviado">
        <ShieldCheck size={44} className="mx-auto text-emerald-600" />
        <h1 className="font-heading font-extrabold text-2xl mt-3">CADASTRO RECEBIDO!</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Seus documentos estão em análise pela equipe MontaJá — prazo de até <b>72 horas</b>.<br />
          O acesso ao portal será liberado somente após a aprovação. Você receberá um WhatsApp.
        </p>
        <Button onClick={() => navigate("/")} variant="outline" className="btn-sharp mt-5">Voltar ao início</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-border">
        <div className="max-w-2xl mx-auto px-6 py-5 flex items-center gap-3">
          <div className="w-7 h-7 brand-bg" /><span className="font-heading font-extrabold">MontaJá</span>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="label-cap"><Building2 size={13} className="inline mr-1" />Cadastro · Montadora (pessoa jurídica)</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">SEJA UMA MONTADORA MONTAJÁ</h1>
        <p className="text-xs text-muted-foreground mt-2">
          Exclusivo para <b>empresas com CNPJ</b>. Após o envio, a análise leva até <b>72h</b> e o acesso
          ao portal só é liberado com a aprovação do MontaJá. O comprovante bancário deve estar
          <b> em nome da empresa</b> (não aceitamos conta de terceiros).
        </p>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          <FF label="Razão social" v={f.nome} on={(v) => upd("nome", v)} req testid="cadm-nome" />
          <FF label="Nome fantasia" v={f.nome_fantasia} on={(v) => upd("nome_fantasia", v)} testid="cadm-fantasia" />
          <FF label="CNPJ" v={f.cnpj} on={(v) => upd("cnpj", v)} req testid="cadm-cnpj" />
          <FF label="Responsável legal" v={f.responsavel} on={(v) => upd("responsavel", v)} req testid="cadm-resp" />
          <FF label="CPF do responsável" v={f.cpf_responsavel} on={(v) => upd("cpf_responsavel", v)} testid="cadm-cpf-resp" />
          <FF label="Telefone" v={f.telefone} on={(v) => upd("telefone", v)} req testid="cadm-tel" />
          <FF label="WhatsApp" v={f.whatsapp} on={(v) => upd("whatsapp", v)} testid="cadm-wpp" />
          <FF label="E-mail" type="email" v={f.email} on={(v) => upd("email", v)} req testid="cadm-email" ac="off" nm="cadm_novo_email" />
          <FF label="Senha" type="password" v={f.password} on={(v) => upd("password", v)} req testid="cadm-senha" ac="new-password" nm="cadm_nova_senha" />
          <FF label="Endereço da empresa" v={f.endereco} on={(v) => upd("endereco", v)} testid="cadm-endereco" />
          <FF label="Chave PIX (em nome da empresa)" v={f.pix} on={(v) => upd("pix", v)} testid="cadm-pix" />
        </div>
        <div className="label-cap mt-6 mb-2">Documentos obrigatórios (PDF ou imagem)</div>
        <div className="grid md:grid-cols-2 gap-3">
          <FileField label="Cartão CNPJ" onChange={(v) => setDocs((d) => ({ ...d, cartao_cnpj: v }))} testid="cadm-doc-cnpj" />
          <FileField label="Contrato Social" onChange={(v) => setDocs((d) => ({ ...d, contrato_social: v }))} testid="cadm-doc-social" />
          <FileField label="Comprovante bancário (em nome da empresa)" onChange={(v) => setDocs((d) => ({ ...d, comprovante_bancario: v }))} testid="cadm-doc-banco" />
          <FileField label="Documentos dos sócios (1 ou mais)" multiple onChange={(v) => setDocs((d) => ({ ...d, docs_socios: v }))} testid="cadm-doc-socios" />
        </div>
        <Button onClick={enviar} disabled={busy}
          className="btn-sharp w-full mt-6 h-12 brand-bg text-white font-bold" data-testid="cadm-enviar">
          {busy ? "ENVIANDO…" : "ENVIAR CADASTRO PARA ANÁLISE (72H)"}
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-3">
          Já tem conta? <Link to="/login" className="underline">Entrar</Link>
        </p>
      </div>
    </div>
  );
}
